import React from 'react';
import { Star, ChevronRight, Sparkles } from 'lucide-react';
import { ProviderLevelBadge } from './ServiceProviderBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ServicePillMobile (Mobile Pill E)
 * Sticky mobile pill for quick service access
 * Shows on product pages when service is relevant
 */
const ServicePillMobile = ({ 
  service, 
  onClick,
  position = 'bottom', // 'bottom' | 'top'
  className = '' 
}) => {
  const {
    id,
    provider_name,
    provider_image,
    service_name,
    rating = 0,
    starting_price,
    currency = 'Rs',
    verification_level = 0,
    is_sponsored = false,
  } = service;

  const imageUrl = provider_image 
    ? (provider_image.startsWith('http') ? provider_image : `${API_URL}${provider_image}`)
    : 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Crect fill="%23e5e7eb" width="100" height="100"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="40" x="50" y="60" text-anchor="middle"%3EP%3C/text%3E%3C/svg%3E';

  const positionClasses = {
    bottom: 'bottom-0 left-0 right-0',
    top: 'top-0 left-0 right-0',
  };

  return (
    <div 
      className={`fixed ${positionClasses[position]} z-50 p-2 bg-white/95 backdrop-blur-sm border-t shadow-lg md:hidden ${className}`}
      data-testid={`service-pill-mobile-${id}`}
    >
      <div 
        className="flex items-center gap-3 p-2 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200 cursor-pointer active:scale-[0.98] transition-transform"
        onClick={() => onClick?.(service)}
      >
        {/* Sponsored Indicator */}
        {is_sponsored && (
          <div className="absolute -top-2 left-4 bg-purple-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
            <Sparkles className="h-3 w-3" />
            <span>Featured</span>
          </div>
        )}
        
        {/* Provider Image */}
        <img
          src={imageUrl}
          alt={provider_name}
          className="w-10 h-10 rounded-full object-cover border-2 border-purple-300 flex-shrink-0"
          onError={(e) => {
            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Crect fill="%23e5e7eb" width="100" height="100"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="40" x="50" y="60" text-anchor="middle"%3EP%3C/text%3E%3C/svg%3E';
          }}
        />
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-gray-900 text-sm truncate">
              {provider_name}
            </h4>
            <ProviderLevelBadge level={verification_level} size="xs" />
          </div>
          <p className="text-xs text-gray-500 truncate">{service_name}</p>
        </div>
        
        {/* Rating & Price */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="text-right">
            <div className="flex items-center gap-1">
              <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />
              <span className="font-semibold text-sm">{rating.toFixed(1)}</span>
            </div>
            <p className="text-sm font-bold text-purple-600">
              {currency} {starting_price?.toLocaleString()}
            </p>
          </div>
          <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center">
            <ChevronRight className="h-5 w-5 text-white" />
          </div>
        </div>
      </div>
      
      {/* Swipe Hint */}
      <p className="text-center text-xs text-gray-400 mt-1">
        Tap to book service • Swipe for more
      </p>
    </div>
  );
};

export default ServicePillMobile;
